import React, { useCallback, useMemo, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import { Skeleton, Stack } from '@mui/material';
import { useDocumentPdf, useDocumentSectionPdf } from '../../../fetch/fetchPdf';
import styled from '@emotion/styled';
import { usePageImage } from '../../../fetch/fetchImage';
import ZoomablePageFab from "../../documentViewCore/ZoomablePageFab";
import { highlightPattern } from '../../documentViewCore/highlightHelpers';

const OverallContainer = styled('div')(({ theme, documentWidth, documentHeight, hasLeft, hasRight }) => {
    let width = `${documentWidth}px`;
    if (hasLeft && hasRight) {
        width = `calc(${documentWidth * 2}px + ${theme.spacing(1)})`
    }
    return ({
        position: 'relative',
        width: width,
        height: `${documentHeight}px`
    });
})

const PageContainer = styled('div')(({ theme, documentWidth, documentHeight, side }) => {
    return ({
        background: theme.palette.background.paper,
        width: documentWidth,
        height: documentHeight,
        marginLeft: side == 'left' ? null : theme.spacing(1)
    });
})

export default function PdfView({ partNumber, sectionFileName, leftPageNumber, rightPageNumber,
    setPageNumber, documentWidth, documentHeight, offset, isOversize, hasCover,
    currentShowZoomFab, nextShowZoomFab, executedQuery }) {
    const [forceReload, setForceReload] = useState(false);

    const { pdfData, isLoading: isPdfLoading } = useDocumentSectionPdf(partNumber, !isOversize ? sectionFileName : null);
    const [docRendered, setDocRendered] = useState(false);
    const [leftDocRendered, setLeftDocRendered] = useState(false);
    const [rightDocRendered, setRightDocRendered] = useState(false);

    const textRenderer = useCallback(
        (textItem) => highlightPattern(textItem.str, executedQuery),
        [executedQuery]
    );

    const forceUpdate = useCallback(() => {
        setForceReload(true);
        setDocRendered(false);
        setLeftDocRendered(false);
        setRightDocRendered(false);
    }, []);

    const completeUpdate = useCallback(() => {
        setForceReload(false);
    }, []);

    useMemo(() => {
        forceUpdate();
    }, [sectionFileName]);

    useMemo(() => {
        if (forceReload) {
            setTimeout(completeUpdate, 10);
        }
    }, [forceReload]);

    const hasLeft = leftPageNumber != null && leftPageNumber != undefined;
    const hasRight = rightPageNumber != null && rightPageNumber != undefined;

    const { image: leftImage, isLoading: leftIsLoading } = usePageImage(hasLeft ? partNumber : null, leftPageNumber + offset);
    const { image: rightImage, isLoading: rightIsLoading } = usePageImage(hasRight ? partNumber : null, rightPageNumber + offset);

    const { pdfData: leftPdf, isLoading: leftPdfIsLoading } = useDocumentPdf(hasLeft && isOversize ? partNumber : null, hasCover ? leftPageNumber + offset + 1 : leftPageNumber + offset);
    const { pdfData: rightPdf, isLoading: rightPdfIsLoading } = useDocumentPdf(hasRight && isOversize ? partNumber : null, hasCover ? rightPageNumber + offset + 1 : rightPageNumber + offset);

    return (
        <OverallContainer className='pdfPageContainer' documentWidth={documentWidth} documentHeight={documentHeight} hasLeft={hasLeft} hasRight={hasRight}>
            <Stack direction='row' justifyContent='center' style={{ position: 'absolute' }}>
                {leftPageNumber != null && leftPageNumber != undefined ? (
                    <PageContainer className='page-container' documentWidth={documentWidth} documentHeight={documentHeight} side='left'>
                        {leftIsLoading ? <Skeleton variant="rectangular" animation="wave" height={documentHeight} width={documentWidth} /> : <img src={leftImage} style={{ height: '100%', width: '100%' }} />}
                    </PageContainer>
                ) : null}
                {rightPageNumber != null && rightPageNumber != undefined ? (
                    <PageContainer className='page-container' documentWidth={documentWidth} documentHeight={documentHeight} side='right'>
                        {rightIsLoading ? <Skeleton variant="rectangular" animation="wave" height={documentHeight} width={documentWidth} /> : <img src={rightImage} style={{ height: '100%', width: '100%' }} />}
                    </PageContainer>
                ) : null}
            </Stack>

            {!forceReload && isOversize && (!leftPdfIsLoading && hasLeft) && (!rightPdfIsLoading && hasRight) ? (
                <div style={{ display: (!leftDocRendered && hasLeft) && (!rightDocRendered && hasRight) ? 'none' : null, position: 'absolute' }}>
                    <Stack direction='row' justifyContent='center'>
                        {!leftPdfIsLoading && !!leftPdf && leftPageNumber != null && leftPageNumber != undefined ? (
                            <Document file={leftPdf} externalLinkTarget="_blank" loading='' onLoadSuccess={(args) => { setTimeout(() => setLeftDocRendered(true), 500); }} onItemClick={({ dest, pageIndex, pageNumber }) => { setPageNumber(pageNumber) }}>
                                <PageContainer className='page-container' documentWidth={documentWidth} documentHeight={documentHeight} side='left'>
                                    <ZoomablePageFab
                                        partNumber={partNumber}
                                        pageNumber={leftPageNumber + offset}
                                        position='left'
                                        alwaysShowOnMobile={currentShowZoomFab}
                                    />
                                    <Page
                                        loading=''
                                        noData=''
                                        pageNumber={1}
                                        renderTextLayer={true}
                                        height={documentHeight}
                                        width={documentWidth}
                                        renderAnnotationLayer={true}
                                        customTextRenderer={textRenderer}
                                    />
                                </PageContainer>
                            </Document>
                        ) : null}

                        {!rightIsLoading && !!rightPdf && rightPageNumber != null && rightPageNumber != undefined ? (
                            <Document file={rightPdf} externalLinkTarget="_blank" loading='' onLoadSuccess={(args) => { setTimeout(() => setRightDocRendered(true), 500); }} onItemClick={({ dest, pageIndex, pageNumber }) => { setPageNumber(pageNumber) }}>
                                <PageContainer className='page-container' documentWidth={documentWidth} documentHeight={documentHeight} side='right'>
                                    <ZoomablePageFab
                                        partNumber={partNumber}
                                        pageNumber={rightPageNumber + offset}
                                        position='right'
                                        alwaysShowOnMobile={nextShowZoomFab}
                                    />
                                    <Page
                                        loading=''
                                        noData=''
                                        pageNumber={1}
                                        renderTextLayer={true}
                                        height={documentHeight}
                                        width={documentWidth}
                                        renderAnnotationLayer={true}
                                        customTextRenderer={textRenderer}
                                    />
                                </PageContainer>
                            </Document>
                        ) : null}
                    </Stack>
                </div>
            ) : null}

            {!forceReload && !isPdfLoading ? (
                <div style={{ display: !docRendered ? 'none' : null, position: 'absolute' }}>
                    <Document file={pdfData} externalLinkTarget="_blank" loading='' onLoadSuccess={(args) => { setTimeout(() => setDocRendered(true), 500) }} onItemClick={({ dest, pageIndex, pageNumber }) => { setPageNumber(pageNumber) }}>
                        <Stack direction='row' justifyContent='center'>
                            {leftPageNumber != null && leftPageNumber != undefined ? (
                                <PageContainer className='page-container' documentWidth={documentWidth} documentHeight={documentHeight} side='left'>
                                    <ZoomablePageFab
                                        partNumber={partNumber}
                                        pageNumber={leftPageNumber + offset}
                                        position='left'
                                        alwaysShowOnMobile={currentShowZoomFab}
                                    />
                                    <Page
                                        loading=''
                                        noData=''
                                        pageNumber={leftPageNumber}
                                        renderTextLayer={true}
                                        height={documentHeight}
                                        width={documentWidth}
                                        renderAnnotationLayer={true}
                                        customTextRenderer={textRenderer}
                                    />
                                </PageContainer>
                            ) : null}
                            {rightPageNumber != null && rightPageNumber != undefined ? (
                                <PageContainer className='page-container' documentWidth={documentWidth} documentHeight={documentHeight} side='right'>
                                    <ZoomablePageFab
                                        partNumber={partNumber}
                                        pageNumber={rightPageNumber + offset}
                                        position='right'
                                        alwaysShowOnMobile={nextShowZoomFab}
                                    />
                                    <Page
                                        loading=''
                                        noData=''
                                        pageNumber={rightPageNumber}
                                        renderTextLayer={true}
                                        height={documentHeight}
                                        width={documentWidth}
                                        renderAnnotationLayer={true}
                                        customTextRenderer={textRenderer}
                                    />
                                </PageContainer>
                            ) : null}
                        </Stack>
                    </Document>
                </div >
            ) : null}
        </OverallContainer>
    );
}